/**
 * 咪咕音乐 AIGC 接口集成
 * 包含：初始化、包月包策略查询、订阅包订购、购物车（功能+订阅包）
 */

// 与 constant.ts 中 MiguConfig 保持一致的渠道号配置
import axios from 'axios';
import { accountUrlHost, javaHost } from '@/bin/javaSetting';
import {
  toast,
} from '@/utils/util'
const MiguDefaultConfig = {
  schannel: '014025D',
};

const MiguCommonPubConfig = {
  schannel: '014X0JQ',
};
function getToken(){
  const params = new URLSearchParams();
  params.append('ww_token', localStorage.getItem('session_id'));
  params.append('channelCode', getMiguChannelCode());
  // /apis 前缀在 ajaxConf 中被映射为 javaHost，这里直接拼接
  return axios.get(`${javaHost}/baohuo-yuanchuang/migu/api/v1/getToken?${params.toString()}`)
    .then(res => res.data);
};
/**
 * 动态获取渠道号（与 constant.ts MiguConfig.schannel 逻辑一致）
 */
function getMiguChannelCode() {
  const commonPub = localStorage.getItem('commonPub');
  const isCommonPub = commonPub === 'true' || commonPub === '1';
  return isCommonPub ? MiguCommonPubConfig.schannel : MiguDefaultConfig.schannel;
}

class MiguRingSDK {
  constructor() {
    this.isInitialized = false;
    this.token = '';
    this.callbacks = {};
  }

  /**
   * 获取当前渠道号（实时读取，与 MiguConfig.schannel 保持同步）
   */
  get channelCode() {
    return getMiguChannelCode();
  }

  /**
   * 1. 初始化方法
   * @param {string} callbackName - 回调方法名（必填）
   * @param {string} customUI - 自定义UI名（可选）
   * @returns {Promise}
   */
  init(callbackName, customUI = '') {
    return new Promise((resolve, reject) => {
      if (!callbackName) {
        reject(new Error('回调方法名为必填参数'));
        return;
      }

      // 检查咪咕SDK是否已加载
      if (typeof window.openMiGuInit_centrality !== 'function') {
        reject(new Error('咪咕SDK未加载，请先引入 migu.js 和 crbtCentralitySDK.js'));
        return;
      }

      // 注册回调函数到全局
      window[callbackName] = async (result) => {
        console.log('咪咕初始化回调:', result);
        if (result && result.resultCode === '0000') {
          this.isInitialized = true;
          const tokenRes = await getToken();
          if (tokenRes && tokenRes.code === 200 && tokenRes.data) {
            token = tokenRes.data.token;
            this.token = token;
          }
          resolve(result);
        } else {
          reject(new Error(result?.resMsg || '初始化失败'));
        }
      };

      // 调用咪咕初始化（channelCode 实时取值）
      window.openMiGuInit_centrality(this.channelCode, callbackName);
    });
  }

  /**
   * 2. 包月包策略查询
   * @param {Object} params
   * @param {string} params.youCallbackName - 回调函数名（必填）
   * @param {string} params.serviceId - 包月包ID（必填，视频彩铃基础会员包id为 698039030000000001）
   * @returns {Promise}
   */
  queryMonthPolicy(params) {
    return new Promise((resolve, reject) => {
      if (!this.isInitialized) {
        reject(new Error('请先调用 init() 初始化'));
        return;
      }

      const { youCallbackName, serviceId } = params;
      const token = params.token || this.token;

      if (!youCallbackName || !token || !serviceId) {
        reject(new Error('缺少必填参数'));
        return;
      }

      if (typeof window.monthPolicy !== 'function') {
        reject(new Error('monthPolicy 方法不存在，请确认SDK已正确加载'));
        return;
      }

      window[youCallbackName] = (result) => {
        console.log('包月包策略查询回调:', result);
        if (result && result.resCode === '000000') {
          resolve(result);
        } else {
          reject(new Error(result?.resMsg || '查询失败'));
        }
      };
      window.monthPolicy({
        youCallbackName,
        channelCode: this.channelCode,
        token,
        serviceId,
      });
    });
  }

  /**
   * 3. 视频彩铃订阅包包月订购接口
   * @param {Object} params
   * @param {string} params.youCallbackName - 回调函数名（必填）
   * @param {string} params.token - 登陆凭证（必填）
   * @param {string} params.cpId - CP商ID（必填）
   * @param {string} params.bizCode - 业务代码（必填）
   * @param {string} params.cpparam - 阳光计划透传字段（必填）
   * @param {string} params.salePrice - 销售价格（必填）
   * @param {string} params.excode - 4位渠道自编码（可选）
   * @param {string} params.defSeq - 接口流程自定义流水号（可选）
   * @returns {Promise}
   */
  orderSubVrbt(params) {
    return new Promise((resolve, reject) => {
      if (!this.isInitialized) {
        reject(new Error('请先调用 init() 初始化'));
        return;
      }

      const { youCallbackName, cpId, bizCode, cpparam, salePrice, excode, defSeq } = params;
      const token = params.token || this.token;

      if (!youCallbackName || !token || !cpId || !bizCode || !cpparam || !salePrice) {
        reject(new Error('缺少必填参数'));
        return;
      }

      if (typeof window.miguOrderSubVrbt_centrality !== 'function') {
        reject(new Error('miguOrderSubVrbt_centrality 方法不存在'));
        return;
      }

      window[youCallbackName] = (result) => {
        console.log('订阅包订购回调:', result);
        if (result && result.resCode === '000000') {
          resolve(result);
        } else {
          reject(new Error(result?.resMsg || '订购失败'));
        }
        if (result?.resMsg) {
          toast(result.resMsg);
        }
      };

      window.miguOrderSubVrbt_centrality({
        youCallbackName,
        channelCode: this.channelCode,
        token,
        cpId,
        bizCode,
        cpparam,
        salePrice,
        excode: excode || '',
        defSeq: defSeq || '',
      });
    });
  }

  /**
   * 4. 购物车（视频彩铃功能+订阅包）
   * @param {Object} params
   * @param {string} params.youCallbackName - 回调函数名（必填）
   * @param {string} params.token - 登陆凭证（必填）
   * @param {string} params.serviceId - 订阅包ID（必填）
   * @returns {Promise}
   */
  openVrbtAndSubVrbt(params) {
    return new Promise((resolve, reject) => {
      if (!this.isInitialized) {
        reject(new Error('请先调用 init() 初始化'));
        return;
      }

      const { youCallbackName, serviceId } = params;
      const token = params.token || this.token;

      if (!youCallbackName || !token || !serviceId) {
        reject(new Error('缺少必填参数'));
        return;
      }

      if (typeof window.openVrbtAndSubVrbt_centrality !== 'function') {
        reject(new Error('openVrbtAndSubVrbt_centrality 方法不存在'));
        return;
      }

      window[youCallbackName] = (result) => {
        console.log('购物车订购回调:', result);
        if (result && result.resCode === '000000') {
          resolve(result);
        } else {
          reject(new Error(result?.resMsg || '订购失败'));
        }
        if (result?.resMsg) {
          toast(result.resMsg);
        }
      };
      console.log('openVrbtAndSubVrbt_centrality',{
        youCallbackName,
        channelCode: this.channelCode,
        token,
        serviceId,
      })
      window.openVrbtAndSubVrbt_centrality({
        youCallbackName,
        channelCode: this.channelCode,
        token,
        serviceId,
      });
    });
  }

  /**
   * 辅助方法：生成唯一回调函数名
   */
  generateCallbackName(prefix = 'miguCallback') {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 辅助方法：清理回调函数
   */
  cleanupCallback(callbackName) {
    if (window[callbackName]) {
      delete window[callbackName];
    }
  }
}

// 导出单例
const miguRingSDK = new MiguRingSDK();

// 兼容不同模块系统
if (typeof module !== 'undefined' && module.exports) {
  module.exports = miguRingSDK;
  module.exports.default = miguRingSDK;
} else if (typeof define === 'function' && define.amd) {
  define([], () => miguRingSDK);
} else {
  window.MiguSDK = miguRingSDK;
}

export default miguRingSDK;
